import { useHead, validateEmail } from "../services/common";

import Footer from "../components/Footer";
import { Header, Input, Loading } from "../components";
import Content from "../components/Content";
import { useState } from "react";
import { Reservation } from "../../types";
import moment from "moment";
import { api, useApi } from "../services/api";
import { useUser } from "../User";
import Icon from "@mdi/react";
import { mdiFileExcel, mdiReload } from "@mdi/js";

const emptyReservation: Reservation = {
    _id: "",
    date: moment().format(),
    name: "",
    surname: "",
    contactPerson: "",
    address: "",
    numberOfBooths: "1",
    email: "",
    goods: "",
    phone: "",
    variableSymbol: "",
};

const sendingMessage = "Přihláška se odesílá...";

export default function ReservationPage() {
    const user = useUser();

    const title = "Rezervace jarmarečního stánku";
    useHead(title, `${title} | Jilemnicko - společenství obcí`);

    const [reservation, setReservation] = useState<Reservation>(emptyReservation);
    const [consent, setConsent] = useState(false);
    const [message, setMessage] = useState("");

    async function handleSend() {
        if (!consent) {
            setMessage("Musíte souhlasit s podmínkami.");
            return;
        }
        if (!reservation.name || !reservation.surname) {
            setMessage("Musíte vyplnit jméno a příjmení.");
            return;
        }
        if (!reservation.address) {
            setMessage("Musíte vyplnit adresu bydliště/sídlo firmy.");
            return;
        }
        if (!reservation.goods) {
            setMessage("Musíte vyplnit prodávané zboží.");
            return;
        }
        if (!reservation.numberOfBooths) {
            setMessage("Musíte vyplnit počet stánků.");
            return;
        }
        if (!reservation.contactPerson) {
            setMessage("Musíte vyplnit jméno kontaktní osoby.");
            return;
        }
        if (!reservation.variableSymbol) {
            setMessage("Musíte vyplnit IČO nebo RČ (variabilní symbol pro platbu).");
            return;
        }
        if (!reservation.phone) {
            setMessage("Musíte vyplnit telefon.");
            return;
        }
        if (!validateEmail(reservation.email)) {
            setMessage("E-mail je neplatný.");
            return;
        }

        setMessage(sendingMessage);
        const [, error] = await api.addReservation(reservation);

        if (error) {
            setMessage("Přihláška se nepodařila odeslat. Zkuste to prosím později.");
            return;
        }

        setMessage("Přihláška byla úspěšně odeslána.");
        setReservation(emptyReservation);
        setConsent(false);
    }

    return <>
        <Header title={title} background="podvecery" />
        <main>
            <section className="content-page">
                <h1 className="hidden">{title}</h1>
                <Content id="reservation" />

                <Input label="Jméno" name="name" value={reservation} onChange={setReservation} />
                <Input label="Příjmení" name="surname" value={reservation} onChange={setReservation} />
                <Input label="Adresa bydliště/sídlo firmy" textarea name="address" value={reservation} onChange={setReservation} />
                <Input label="IČO nebo RČ (variabilní symbol pro platbu)" name="variableSymbol" value={reservation} onChange={setReservation} />
                <Input label="Prodávané zboží" textarea name="goods" value={reservation} onChange={setReservation} />
                <Input label="Počet stánků" name="numberOfBooths" value={reservation} onChange={setReservation} />
                <Input label="Jméno kontaktní osoby" name="contactPerson" value={reservation} onChange={setReservation} />
                <Input label="Telefon" name="phone" value={reservation} onChange={setReservation} />
                <Input label="E-mail" name="email" value={reservation} onChange={setReservation} />

                <p>Vyplněním a odesláním tohoto formuláře poskytuji Jilemnicku - společenství obcí coby správci osobních údajů, souhlas se zpracováním níže uvedených osobních údajů, a to pro účely evidence v rezervačním systému stánkařů „Krakonošovy letní podvečery“ a na dobu trvání příprav a realizaci této akce.</p>
                <p>Souhlas je udělen dle zákona č. 101/2000 Sb. o ochraně osobních údajů a o změně některých zákonů, ve znění pozdějších předpisů, a Obecném nařízení a ochraně osobních údajů (2016/679) a to na výše uvedenou dobu po udělení souhlasu, příp. do odvolání tohoto souhlasu.</p>

                <label><input type="checkbox" checked={consent} onChange={() => setConsent(!consent)} /> Souhlasím s podmínkami</label>

                {message && <p className="alert">{message}</p>}

                {message !== sendingMessage && <button className="center" onClick={handleSend}>Závazně odeslat přihlášku k rezervaci stánku</button>}
                {message === sendingMessage && <Loading />}
            </section>

            {user.isAdmin && <AdminReservation />}
        </main>
        <Footer />
    </>;
}

function AdminReservation() {
    const [result, error, isLoading, status, reload] = useApi.getReservations({});

    const [selectedYear, setSelectedYear] = useState(moment().year());

    const years = result ? Array.from(new Set(result.map(r => moment(r.date).year()))).sort((a, b) => b - a) : [];
    const filteredResult = result ? result.filter(r => moment(r.date).year() === selectedYear) : [];

    function handleSaveTable(event: any) {
        if (filteredResult.length === 0) {
            event.preventDefault();
            return;
        }

        function d(s: string): string {
            return `"${s.replace(/;/g, ',').replace(/"/g, '\\"')}"`;
        }

        let csvString = `Čas přihlášky;Jméno;Příjmení;Počet stánků;Kontaktní osoba;E-mail;Telefon;Variabilní symbol;Adresa;Prodávané zboží
${filteredResult.sort((a, b) => b.date.localeCompare(a.date)).map(row => `${moment(row.date).format("DD. MM. YYYY hh:mm:ss")};${d(row.name)};${d(row.surname)};${d(row.numberOfBooths)};${d(row.contactPerson)};${d(row.email)};${d(row.phone)};${d(row.variableSymbol)};${d(row.address)};${d(row.goods)}`).join("\n")}`;

        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]); // BOM pro UTF-8
        const blob = new Blob([bom, csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const a = event.target as HTMLLinkElement;
        a.href = url;
    }

    return <section className="content-page">
        <h2>Administrátorská sekce rezervací</h2>

        <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} style={{ width: "200px", padding: "10px" }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
            {!years.includes(moment().year()) && <option value={moment().year()}>{moment().year()}</option>}
        </select>

        <div className="buttons" style={{ display: "inline-flex", gap: "20px", marginBottom: "20px", alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={reload}><Icon path={mdiReload} size={1} /> Přenačíst tabulku</button>
            <a className="button" href="#" download={`rezervace-${selectedYear}.csv`} onClick={handleSaveTable}><Icon path={mdiFileExcel} size={1} />  Uložit tabulku do souboru</a>
        </div>

        {isLoading && <Loading />}
        {error && <p className="alert">{error}</p>}
        {result && <table className="data-table">
            <thead>
                <tr>
                    <th>Čas přihlášky</th>
                    <th>Jméno</th>
                    <th>Příjmení</th>
                    <th>Počet stánků</th>
                    <th>Kontaktní osoba</th>
                    <th>E-mail</th>
                    <th>Telefon</th>
                    <th>Variabilní symbol</th>
                    <th>Adresa</th>
                    <th>Prodávané zboží</th>
                </tr>
            </thead>
            <tbody>
                {filteredResult.sort((a, b) => b.date.localeCompare(a.date)).map(r => <tr key={r._id}>
                    <td>{moment(r.date).format("DD. MM. YYYY hh:mm:ss")}</td>
                    <td>{r.name}</td>
                    <td>{r.surname}</td>
                    <td>{r.numberOfBooths}</td>
                    <td>{r.contactPerson}</td>
                    <td>{r.email}</td>
                    <td>{r.phone}</td>
                    <td>{r.variableSymbol}</td>
                    <td>{r.address}</td>
                    <td>{r.goods}</td>
                </tr>)}
            </tbody>
        </table>}
        {filteredResult && filteredResult.length === 0 && <p className="info">V tomto roce zatím nejsou žádné rezervace.</p>}
    </section>;
}

